import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ProjectCard, Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { PromptInput } from "@/components/ui/input";
import { LoadingSpinner, GeneratingIndicator, Skeleton, RenderProgress } from "@/components/ui/loading";

/**
 * DesignSystemTest - Página de teste para Checkpoint 1
 * 
 * Testes:
 * 1. Vácuo OLED (#020202 vs #000000)
 * 2. Legibilidade Input em luz solar
 * 3. Animações (levitate, aura-breathe)
 * 4. GeneratingIndicator 60fps
 * 5. Aspect Ratio 9:16
 */
export default function DesignSystemTest() {
    const [isGenerating, setIsGenerating] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentFrame, setCurrentFrame] = useState(0);

    const startGeneration = () => {
        setIsGenerating(true);
        setProgress(0);
        setCurrentFrame(0);

        const interval = setInterval(() => {
            setProgress(prev => {
                if (prev >= 100) {
                    clearInterval(interval);
                    setIsGenerating(false);
                    return 100;
                }
                setCurrentFrame(Math.floor((prev + 1) * 1.8));
                return prev + 1;
            });
        }, 100);
    };

    return (
        <div className="min-h-screen bg-void-absolute text-textPrimary">
            {/* TESTE 1: Vácuo OLED */}
            <section className="p-4 border-b border-void-warm">
                <h2 className="text-headline text-gradient-gold mb-4">Teste 1: Vácuo OLED</h2>
                <div className="flex gap-4">
                    <div className="flex-1 h-32 bg-[#020202] rounded-bold flex items-center justify-center">
                        <span className="text-caption text-textMuted">#020202</span>
                    </div>
                    <div className="flex-1 h-32 bg-[#000000] rounded-bold flex items-center justify-center">
                        <span className="text-caption text-textMuted">#000000</span>
                    </div>
                </div>
                <p className="text-micro text-textMuted mt-2">
                    ⚠️ Em OLED, ambos devem parecer IDÊNTICOS
                </p>
            </section>

            {/* TESTE 2: Legibilidade Input */}
            <section className="p-4 border-b border-void-warm">
                <h2 className="text-headline text-gradient-gold mb-4">Teste 2: Input Terminal</h2>
                <PromptInput placeholder="Digite seu prompt aqui..." />
                <p className="text-micro text-textMuted mt-2">
                    ⚠️ Teste em LUZ SOLAR DIRETA - caret deve ser visível
                </p>
            </section>

            {/* TESTE 3: Animações */}
            <section className="p-4 border-b border-void-warm">
                <h2 className="text-headline text-gradient-gold mb-4">Teste 3: Animações</h2>
                <div className="flex gap-4">
                    <Card className="flex-1 animate-levitate">
                        <CardHeader>
                            <CardTitle className="text-title">Levitate</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-caption text-textSecondary">6s ease-in-out</p>
                        </CardContent>
                    </Card>
                    <Card className="flex-1">
                        <CardHeader>
                            <CardTitle className="text-title animate-aura-breathe text-gold">Aura Breathe</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-caption text-textSecondary">3s opacity pulse</p>
                        </CardContent>
                    </Card>
                </div>
            </section>

            {/* TESTE 4: GeneratingIndicator 60fps */}
            <section className="p-4 border-b border-void-warm">
                <h2 className="text-headline text-gradient-gold mb-4">Teste 4: GeneratingIndicator</h2>

                {!isGenerating ? (
                    <Button variant="coral" onClick={startGeneration} className="w-full">
                        Iniciar Teste de Geração
                    </Button>
                ) : (
                    <div className="space-y-4">
                        <GeneratingIndicator
                            text="Renderizando..."
                            frameCount={{ current: currentFrame, total: 180 }}
                        />
                        <RenderProgress progress={progress} label="Progresso" />
                    </div>
                )}

                <p className="text-micro text-textMuted mt-2">
                    ⚠️ Ative FPS counter - deve manter 60fps
                </p>
            </section>

            {/* TESTE 5: Aspect Ratio 9:16 */}
            <section className="p-4 border-b border-void-warm">
                <h2 className="text-headline text-gradient-gold mb-4">Teste 5: ProjectCard 9:16</h2>
                <div className="flex gap-4 justify-center">
                    <ProjectCard aspectRatio="vertical" className="w-40">
                        <div className="absolute inset-0 flex items-end p-4">
                            <span className="text-caption text-textPrimary bg-void-deep/80 px-2 py-1 rounded-micro">
                                9:16 Vertical
                            </span>
                        </div>
                    </ProjectCard>
                    <ProjectCard aspectRatio="horizontal" className="w-64">
                        <div className="absolute inset-0 flex items-end p-4">
                            <span className="text-caption text-textPrimary bg-void-deep/80 px-2 py-1 rounded-micro">
                                16:9 Horizontal
                            </span>
                        </div>
                    </ProjectCard>
                </div>
                <p className="text-micro text-textMuted mt-2">
                    ⚠️ Teste em iPhone SE E iPhone 14 Pro Max
                </p>
            </section>

            {/* Componentes Adicionais */}
            <section className="p-4">
                <h2 className="text-headline text-gradient-gold mb-4">Componentes Extras</h2>

                <div className="space-y-4">
                    <div className="flex gap-4">
                        <Button variant="default">Gold CTA</Button>
                        <Button variant="coral">Coral CTA</Button>
                        <Button variant="secondary">Secondary</Button>
                        <Button variant="outline">Outline</Button>
                    </div>

                    <div className="flex gap-4 items-center">
                        <LoadingSpinner size="sm" />
                        <LoadingSpinner size="md" />
                        <LoadingSpinner size="lg" />
                    </div>

                    <div className="space-y-2">
                        <Skeleton variant="text" className="w-3/4" />
                        <Skeleton variant="text" className="w-1/2" />
                        <Skeleton variant="avatar" />
                    </div>
                </div>
            </section>

            {/* Footer com Touch Target */}
            <footer className="fixed bottom-0 inset-x-0 bg-void-deep border-t border-void-warm p-4">
                <div className="flex justify-around">
                    <Button variant="ghost" size="icon" className="touch-target">
                        🏠
                    </Button>
                    <Button variant="coral" size="icon" className="touch-target">
                        ➕
                    </Button>
                    <Button variant="ghost" size="icon" className="touch-target">
                        📚
                    </Button>
                    <Button variant="ghost" size="icon" className="touch-target">
                        👤
                    </Button>
                </div>
                <p className="text-micro text-center text-textMuted mt-2">
                    Touch targets: 56dp mínimo
                </p>
            </footer>
        </div>
    );
}
